import { createElement, useRef } from "react"
import type { FC, ReactElement, RefObject } from "react"

import { AnimatePresence } from "framer-motion"
import isEmpty from "lodash-es/isEmpty"
import isUndefined from "lodash-es/isUndefined"
import map from "lodash-es/map"
import upperFirst from "lodash-es/upperFirst"
import { useCss } from "react-use"

import { ETypographySize, ErrorHint } from "@/3514/components"
import { THEME } from "@/3514/theme"
import { buildCssClassName, isBrowserVersionGreater, isFirefoxBrowser } from "@/3514/utils"

import assets from "./input.assets"
import styles from "./input.styles"
import { EInputVariant, type TInputProps } from "./input.types"

const assetsMap: Record<string, (cn: string) => ReactElement> = {
    lock: (cn: string): ReactElement => createElement(assets.LockIcon, { className: cn })
}

const isSpellingClassSupported: boolean =
    !isFirefoxBrowser() &&
    isBrowserVersionGreater("edge", "121") &&
    isBrowserVersionGreater("chrome", "121") &&
    isBrowserVersionGreater("safari", "17.4")

const Input: FC<TInputProps> = ({
    ref,
    name,
    label = String(""),
    type,
    variant = EInputVariant.Whitish,
    placeholder = String(""),
    textSize = ETypographySize.Medium,
    onClick = undefined,
    onChange = undefined,
    onKeyDown = undefined,
    onFocus = undefined,
    onBlur = undefined,
    wrapperClasses = String(""),
    inputClasses = String(""),
    iconType = undefined,
    isDisabled = false,
    isRequired = false,
    isError = false,
    errorMessages = undefined,
    withSpelling = false,
    withSpellingCssOverride = false,
    mode = "text",
    autoComplete = undefined,
    maxSymbols = undefined,
    enterKeyHint = undefined,
    wrapperDirection = "column",
    inputHeight = undefined,
    withErrorMessagesOffset = true,
    withTitles = true
}: TInputProps): ReactElement => {
    const localRef: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null)

    const labelRef: RefObject<HTMLLabelElement> = useRef<HTMLLabelElement>(null)

    const insertedClasses: string = useCss({
        ...(Boolean(withSpellingCssOverride && isSpellingClassSupported) && {
            "&::spelling-error": {
                textDecoration: "none",
                backgroundColor: THEME.COLORS.red[300]
            }
        })
    })

    const currentState: string = styles.colors[variant][isError ? "error" : isDisabled ? "disabled" : "default"]({
        isFilled: !isEmpty(localRef.current?.value)
    })

    const withLabel: boolean = !!label

    const placeholderValue: string = isEmpty(placeholder) ? upperFirst<string>(name) : placeholder

    return (
        <div
            className={buildCssClassName(styles.classes.container(), {
                [wrapperClasses]: wrapperClasses
            })}
        >
            <div className={styles.classes.wrapper({ isColumn: wrapperDirection === "column" })}>
                <label ref={labelRef} htmlFor={name} className={styles.classes.label({ isVisible: withLabel })}>
                    {label || name}
                </label>
                <input
                    ref={(element: HTMLInputElement): void => {
                        ref(element)
                        localRef.current = element
                    }}
                    id={name}
                    name={name}
                    type={type}
                    placeholder={isRequired ? `${placeholder}*` : placeholder}
                    title={withTitles ? placeholderValue : undefined}
                    onClick={onClick}
                    onChange={onChange}
                    onKeyDown={onKeyDown as never}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    disabled={isDisabled}
                    required={isRequired}
                    tabIndex={0}
                    spellCheck={withSpelling}
                    inputMode={mode}
                    autoComplete={autoComplete}
                    maxLength={maxSymbols}
                    enterKeyHint={enterKeyHint}
                    aria-required={isRequired}
                    aria-invalid={isError}
                    aria-label={label || name}
                    aria-describedby={isError && !isEmpty(errorMessages) ? `${name}-error-0` : undefined}
                    aria-placeholder={placeholder}
                    style={{ ...(inputHeight && { height: inputHeight }) }}
                    className={buildCssClassName({
                        [styles.classes.input({ textSize })]: true,
                        [insertedClasses]: insertedClasses,
                        [currentState]: variant,
                        [inputClasses]: inputClasses,
                        "cursor-not-allowed": isDisabled
                    })}
                />
            </div>

            {!isUndefined(iconType) && assetsMap[iconType](styles.classes.icon())}

            <AnimatePresence mode="sync" presenceAffectsLayout initial={false}>
                {!isEmpty(errorMessages) &&
                    map<string, ReactElement>(
                        errorMessages,
                        (m: string, index: number): ReactElement => (
                            <ErrorHint
                                key={`${name}-${m}`}
                                id={`${name}-error-${index}`}
                                withNoAnimatePresence
                                motionProps={{
                                    layout: "position",
                                    layoutId: `${name}-${m}`,
                                    ...(withLabel &&
                                        withErrorMessagesOffset && {
                                            style: { marginLeft: `${labelRef.current?.clientWidth}px` }
                                        })
                                }}
                                isVisible
                                text={m}
                                variant="error"
                                position="left"
                                role="alert"
                                isBorderless
                            />
                        )
                    )}
            </AnimatePresence>
        </div>
    )
}

Input.displayName = "Input"

export { Input }
